import { FeedType } from '@dehaat/kisan-app-bl/models/Feed';
import Image from 'next/image';
import Link from 'next/link';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AVAILABLE_STOCK } from '@/constants/common';
import { CartContext } from '@/context/CartProvider';
import { Product, Product2, ProductVariant } from '@/models/Product';
import styles from '@/styles/HideScrollbar.module.css';
import truncateStyles from '@/styles/TruncateContent.module.css';
import { ProductListTypeEnum } from '@/utils/analytics/types';
import { formatProductData } from '@/utils/cart';
import { isArray } from '@/utils/helper';
import transformProductInventorySet from '@/utils/helpers/transformProductInventorySet';
import BottomDrawer from '../BottomDrawer';
import ProductIcon from '../icons/ProductIcon';
import HyperLocalAddToCart from '../products/HyperLocalAddToCart';
import ProductPrice from '../products/ProductPrice';
import ProductVariantList from '../products/ProductVariantList';
import Header from './SectionHeader';
interface Props {
  metaData: FeedType;
}
interface ProductCardProps {
  product: Product;
  position: number;
}
const getActiveVariants = (variants: ProductVariant[], listType?: ProductListTypeEnum) => {
  return variants.filter(variant => {
    if (listType === ProductListTypeEnum.SEARCH) {
      return isArray(variant.inventory_set);
    }
    return variant.active && isArray(variant.inventory_set);
  });
};
const isProduct2 = (item: unknown): item is Product2 => {
  return typeof item === 'object' && item !== null && 'product_variants' in item;
};
const ProductShorts = ({
  metaData
}: Props) => {
  const products = metaData.data;
  return <section className="" data-sentry-component="ProductShorts" data-sentry-source-file="ProductShorts.tsx">
      <Header title="Shop Product: Watch, Explore & Buy" data-sentry-element="Header" data-sentry-source-file="ProductShorts.tsx" />
      <div className={`overflow-y-scroll space-x-4 md:space-x-6 flex px-4 mt-4 ${styles.scroll}`}>
        {products.map((item, index) => {
        if (!isProduct2(item)) {
          return;
        }
        const product = transformProductInventorySet(item);
        return <ProductCard key={index} product={product} position={index} />;
      })}
      </div>
    </section>;
};
const ProductCard = ({
  product,
  position
}: ProductCardProps) => {
  const [activeVariants, setActiveVariants] = useState<ProductVariant[]>([]);
  const [showVariantDrawer, setShowVariantDrawer] = useState(false);
  const [isUnavailableAtVendor, setIsUnavailableAtVendor] = useState(false);
  const {
    cartItems
  } = useContext(CartContext);
  const StockInSS = useMemo(() => sessionStorage?.getItem(AVAILABLE_STOCK),
  // We need updated stock details everything quantity changes for any product
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [cartItems]);
  const productSlug = product?.slug?.slug;
  const productGif = product?.product_media || '';
  const imageURl = product.image_url;
  const availableStock = useMemo(() => StockInSS ? JSON.parse(StockInSS) : undefined, [StockInSS]);
  const isMultipleVariantsInCart = useMemo(() => {
    if (activeVariants.length > 1) {
      return activeVariants.some(variant => cartItems[variant.id]);
    }
    return false;
  }, [activeVariants, cartItems]);
  const hasMultiVariantProductWithItems = isMultipleVariantsInCart;
  const selectedVariant = useMemo(() => {
    return activeVariants.find(variant => cartItems[variant.id]) || activeVariants[0];
  }, [activeVariants, cartItems]);
  const quantity = useMemo(() => {
    if (hasMultiVariantProductWithItems) {
      return activeVariants.reduce((total, variant) => {
        return total + (cartItems[variant.id] || 0);
      }, 0);
    }
    return cartItems[selectedVariant?.id] || 0;
  }, [activeVariants, cartItems, hasMultiVariantProductWithItems, selectedVariant?.id]);
  const isOutOfStock = activeVariants.length === 0 || isUnavailableAtVendor;
  const toggleBottomDrawer = useCallback(() => {
    if (activeVariants.length > 1) {
      setShowVariantDrawer(prev => !prev);
    }
  }, [activeVariants.length]);
  useEffect(() => {
    if (isArray(product.product_variants)) {
      const formatedProduct = formatProductData(product);
      setActiveVariants(getActiveVariants(formatedProduct.product_variants, ProductListTypeEnum.PRODUCT));
    }
  }, [product, StockInSS]);
  useEffect(() => {
    if (!availableStock || activeVariants.length === 0) {
      setIsUnavailableAtVendor(false);
      return;
    }
    setIsUnavailableAtVendor(activeVariants.every(variant => availableStock[variant.id] === 0));
  }, [activeVariants, availableStock]);
  return <>
      <div className="bg-gray-20 rounded-xl min-w-[210px] w-[210px] flex flex-col">
        <Link href={`/product/${encodeURIComponent(productSlug)}?ps=1`} className="block" data-sentry-element="Link" data-sentry-source-file="ProductShorts.tsx">
          <div className="w-full aspect-[9/16] min-h-[198px] rounded-lg overflow-hidden bg-gray-100">
            {productGif ? <Image src={productGif} alt={`${product.name} Benefits`} width={480} height={854} className="w-full h-full object-cover" unoptimized /> : null}
          </div>
        </Link>
        <div className="p-2 flex flex-1 flex-col">
          <div className="flex">
            <div className={`aspect-square p-2 shrink-0 grow-0 relative flex items-center justify-center w-16 h-16 rounded-lg bg-white`}>
              {imageURl ? <Image src={imageURl} alt={product.name} width={80} height={80} className={`object-contain`} style={{
              width: 'auto',
              height: '100%'
            }} /> : <ProductIcon />}
            </div>
            <div className="ml-2">
              <p className={`text-sm font-semibold ${truncateStyles.truncate_after_two_lines} h-[40px]`}>
                {product.name}
              </p>
              {!hasMultiVariantProductWithItems && !isOutOfStock ? <ProductPrice variant={selectedVariant} /> : null}
            </div>
          </div>
          <div className="mt-2 min-h-[50px] flex-1">
            {hasMultiVariantProductWithItems ? <div className={`flex flex-col gap-y-1 max-h-[50px] min-h-[50px] overflow-y-auto pr-1 ${styles.scroll}`}>
                {activeVariants.map(variant => cartItems[variant.id] ? <button className="flex justify-between items-center px-2 py-1 border border-gray-300 rounded-md bg-white/70 " key={variant.id} onClick={toggleBottomDrawer} type="button">
                      <p className="font-medium text-[#33393D] text-sm truncate pr-2">
                        {variant.attribute_value[0]?.name}
                      </p>
                      <p className="bg-primary-100 px-2 py-0.5 rounded-xl text-white text-xs shrink-0">
                        {cartItems[variant.id]}
                      </p>
                    </button> : null)}
              </div> : null}
          </div>
          <div className="mt-2">
            {selectedVariant ? <HyperLocalAddToCart productVariant={selectedVariant} listType={ProductListTypeEnum.PRODUCT} isOutOfStock={isOutOfStock} length={activeVariants.length} onClick={toggleBottomDrawer} quantity={quantity} product={product} /> : null}
          </div>
        </div>
      </div>
      {activeVariants.length > 1 ? <BottomDrawer show={showVariantDrawer} onClose={() => setShowVariantDrawer(false)} drawerStyle={'md:max-w-xl'} containScroll>
          <section className="flex flex-col min-h-0 max-h-[90vh] bg-gray-10 shadow-[0_-4px_4px_rgba(0,0,0,0.07)] px-4 pt-4 pb-6 rounded-t-xl">
            <ProductVariantList productVariants={activeVariants} listType={ProductListTypeEnum.PRODUCT} position={position} onContine={() => setShowVariantDrawer(false)} product={product} />
          </section>
        </BottomDrawer> : null}
    </>;
};
export default ProductShorts;